import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'

interface SheetProps {
  open: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  dismissible?: boolean
}

/**
 * Bottom sheet — the mobile-native way to show detail without losing the list
 * behind it. Blocking sheets keep focus trapped and body scroll locked until their
 * required action is complete.
 */
export function Sheet({ open, onClose, title, children, dismissible = true }: SheetProps) {
  const panel = useRef<HTMLDivElement>(null)
  const restoreTo = useRef<HTMLElement | null>(null)
  /*
   * Read through refs so the effect below depends on `open` alone. Callers pass
   * `onClose={() => setX(false)}` - a new function every render - and with it in the
   * dependency list the effect re-ran on every keystroke inside the sheet, each time
   * moving focus back to the first button. On the idea board that was the Close
   * button: type a letter, lose the textarea.
   */
  const onCloseRef = useRef(onClose)
  const dismissibleRef = useRef(dismissible)
  onCloseRef.current = onClose
  dismissibleRef.current = dismissible

  useEffect(() => {
    if (!open) return

    restoreTo.current = document.activeElement as HTMLElement | null
    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'

    // Move focus in, so a screen reader lands on the sheet rather than the page under it.
    const focusable = panel.current?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    )
    ;(focusable ?? panel.current)?.focus()

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        if (dismissibleRef.current) onCloseRef.current()
        return
      }
      if (e.key !== 'Tab' || !panel.current) return

      const items = [
        ...panel.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ]
      if (items.length === 0) return
      const first = items[0]
      const last = items[items.length - 1]

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      document.body.style.overflow = overflow
      restoreTo.current?.focus?.()
    }
  }, [open])

  if (!open) return null

  return createPortal(
    <>
      <div className="sheet__scrim" onClick={dismissible ? onClose : undefined} aria-hidden="true" />
      <div
        ref={panel}
        className={`sheet${dismissible ? '' : ' sheet--blocking'}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
      >
        <div className="sheet__grip" aria-hidden="true" />
        {dismissible ? (
          <button className="sheet__close" onClick={onClose} aria-label="Close">
            <X size={20} strokeWidth={1.4} />
          </button>
        ) : null}
        <div className="sheet__body">{children}</div>
      </div>
    </>,
    document.body,
  )
}
